/**
 * PersonaDetail — Fase 4 drill-down documental (req#7). Al hacer clic en una
 * persona relacionada del árbol UBO (OwnersPanel) se abre su vista: cabecera con
 * rol / posición / participación / patrimonio / país de residencia fiscal y badges
 * PEP·US person, y una sección de Documentos con SOLO los documentos exigidos a ESA
 * persona (`requiredDocsForPerson`), marcando cumplido/faltante contra sus documentos
 * reales (`listDocuments` con `ownerId`) y permitiendo subir a cada ranura
 * (`uploadDocument` con `ownerId`). Reusa los primitivos del sistema de diseño;
 * cadenas es-PA únicamente (mismo patrón literal que OwnersPanel/DocumentsPanel).
 */
import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  requiredDocsForPerson,
  DOC_TYPE_LABELS,
  ALLOWED_DOCUMENT_MIME_TYPES,
  type DocTypeKey,
  type ClientEntityType,
  type EntityMode,
  type OwnerRol,
} from '@cumpli/shared';
import { api } from '../lib/api';
import { useExpediente } from '../lib/hooks';
import { useToast } from '../lib/use-toast';
import { Button, Card, EmptyState, Pill, Skeleton } from '../components/ui';
import { PersonaIcon } from '../components/icons';
import {
  ArrowLeft,
  Check,
  FileText,
  ShieldAlert,
  ShieldCheck,
  TriangleAlert,
  Upload,
} from 'lucide-react';

const ACCEPT = ALLOWED_DOCUMENT_MIME_TYPES.join(',');

/** Etiqueta es-PA del rol de la persona relacionada (Sociedad Anónima o Fundación). */
const ROL_LABEL: Record<OwnerRol, string> = {
  director: 'Director',
  dignatario: 'Dignatario',
  accionista: 'Accionista',
  beneficiario_final: 'Beneficiario final',
  fundador: 'Fundador',
  consejo_fundacional: 'Consejo fundacional',
};

/** El tipo de entidad del cliente que la matriz de documentos entiende. El campo
 *  tipado (`expedientes.entity_type`) aún no llega a la forma UI del expediente
 *  (phase1.ts, congelado por PR #250), así que lo derivamos de `persona`: la
 *  distinción Sociedad↔Fundación no cambia el set base de una persona (solo el
 *  beneficiario secundario de Fundación, que tratamos aparte por `posicion`). */
function entityTypeFromPersona(persona: 'natural' | 'legal'): ClientEntityType {
  return persona === 'natural' ? 'persona_natural' : 'sociedad_anonima';
}

function formatPct(pct: number | null | undefined): string {
  if (pct == null || !Number.isFinite(pct)) return '—';
  return `${Math.round(pct * 100) / 100}%`;
}

export default function PersonaDetail() {
  const { id, ownerId } = useParams<{ id: string; ownerId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const { data: exp, isLoading: expLoading } = useExpediente(id);
  const { data: settings } = useQuery({
    queryKey: ['tenant-settings'],
    queryFn: api.getTenantSettings,
  });
  // Same key as OwnersPanel so the owner list is a shared cache entry.
  const { data: owners, isLoading: ownersLoading } = useQuery({
    queryKey: ['expedientes', id, 'owners'],
    queryFn: () => api.listOwners(id!),
    enabled: !!id,
  });
  const { data: docs, isLoading: docsLoading } = useQuery({
    queryKey: ['expedientes', id, 'documents', 'owner', ownerId],
    queryFn: () => api.listDocuments(id!, { ownerId: ownerId! }),
    enabled: !!id && !!ownerId,
  });

  // Hidden file input reused by every slot's "Subir" button.
  const fileRef = useRef<HTMLInputElement>(null);
  const [pendingSlot, setPendingSlot] = useState<{ key: DocTypeKey; label: string } | null>(null);

  const uploadMutation = useMutation({
    mutationFn: (input: { file: File; category: string }) =>
      api.uploadDocument(id!, { file: input.file, category: input.category, ownerId: ownerId! }),
    onSuccess: async (_data, input) => {
      // Prefix-invalidate so the entity Documentos tab refreshes too.
      await qc.invalidateQueries({ queryKey: ['expedientes', id, 'documents'] });
      toast({ message: `Documento subido — ${input.file.name}`, kind: 'success' });
    },
    onError: (err) =>
      toast({
        message: err instanceof Error ? err.message : 'No se pudo subir el documento',
        kind: 'error',
      }),
  });

  const loading = expLoading || ownersLoading;
  const owner = owners?.find((o) => o.id === ownerId);

  if (loading) {
    return (
      <div className="grid gap-4">
        <Skeleton className="h-24 w-full rounded-2xl" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }

  if (!exp || !owner) {
    return (
      <EmptyState
        icon={<TriangleAlert className="h-8 w-8 text-tone-red" strokeWidth={1.5} />}
        title="Persona no encontrada"
        hint="La persona no existe o no pertenece a este expediente."
        action={
          <Button variant="outline" onClick={() => navigate(`/expedientes/${id}`)}>
            Volver al expediente
          </Button>
        }
      />
    );
  }

  const sector: EntityMode = settings?.entityMode ?? 'financiero';
  const entityType = entityTypeFromPersona(exp.persona);
  const rol: OwnerRol = owner.rol ?? 'beneficiario_final';
  const posicion = owner.posicion ?? undefined;
  // Fundación patrimonial: al beneficiario secundario no se le pide documentación
  // individual (criterio por posición, no por umbral).
  const esSecundario = posicion === 'secundario';
  const required: DocTypeKey[] = esSecundario
    ? []
    : requiredDocsForPerson({ sector, entityType, rol, ...(posicion ? { posicion } : {}) });

  // Un documento cumple una ranura cuando su categoría coincide con la etiqueta del
  // tipo (los uploads de esta vista fijan la categoría a esa etiqueta).
  const docsForLabel = (label: string) => (docs ?? []).filter((d) => d.category === label);
  const matchedIds = new Set(required.flatMap((k) => docsForLabel(DOC_TYPE_LABELS[k]).map((d) => d.id)));
  const extras = (docs ?? []).filter((d) => !matchedIds.has(d.id));

  function onPickFile(key: DocTypeKey) {
    setPendingSlot({ key, label: DOC_TYPE_LABELS[key] });
    fileRef.current?.click();
  }
  function onFileChosen(file: File | undefined) {
    const slot = pendingSlot;
    setPendingSlot(null);
    if (fileRef.current) fileRef.current.value = '';
    if (file && slot) uploadMutation.mutate({ file, category: slot.label });
  }

  return (
    <>
      {/* breadcrumb */}
      <div className="mb-4 flex flex-wrap items-center gap-1.5 text-[11px] text-ink-faint">
        <span className="font-semibold uppercase tracking-wider text-primary">Registro</span>
        <span>/</span>
        <button onClick={() => navigate('/expedientes')} className="hover:text-ink transition-colors">
          Mis clientes
        </button>
        <span>/</span>
        <button
          onClick={() => navigate(`/expedientes/${id}`)}
          className="max-w-[40vw] truncate hover:text-ink transition-colors"
        >
          {exp.name}
        </button>
        <span>/</span>
        <span className="text-ink-soft">Beneficiarios</span>
      </div>

      <Button variant="ghost" className="mb-4" onClick={() => navigate(`/expedientes/${id}`)}>
        <ArrowLeft className="h-4 w-4" strokeWidth={2} /> Volver al expediente
      </Button>

      {/* header */}
      <Card>
        <div className="flex flex-wrap items-start gap-4">
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-3xl bg-primary/10">
            <PersonaIcon persona={owner.ownerType} className="h-7 w-7 text-primary-dark" />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="truncate font-serif text-2xl font-medium text-ink">{owner.name}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {owner.rol && <Pill tone="slate">{ROL_LABEL[owner.rol]}</Pill>}
              {owner.posicion && (
                <Pill tone={owner.posicion === 'primario' ? 'blue' : 'slate'}>
                  {owner.posicion === 'primario' ? 'Primario' : 'Secundario'}
                </Pill>
              )}
              {owner.pepPropio && <Pill tone="red">PEP</Pill>}
              {owner.pepFamiliar && <Pill tone="amber">PEP familiar</Pill>}
              {owner.usPerson && <Pill tone="amber">US person</Pill>}
            </div>
          </div>
        </div>

        <dl className="mt-5 grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Participación" value={formatPct(owner.ownershipPct)} />
          <Field label="Participación patrimonial" value={formatPct(owner.porcentajePatrimonial)} />
          <Field label="País de residencia fiscal" value={owner.paisResidenciaFiscal ?? '—'} />
          <Field label="Identificación" value={owner.idNumber ?? '—'} />
          <Field label="País" value={owner.country ?? '—'} />
          <Field label="Tipo" value={owner.ownerType === 'natural' ? 'Persona natural' : 'Persona jurídica'} />
        </dl>
      </Card>

      {/* documentos requeridos de esta persona */}
      <Card className="mt-6">
        <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-serif text-xl font-medium text-ink">Documentos de la persona</h2>
          {!esSecundario && required.length > 0 && (
            <span className="font-mono text-sm text-ink-soft">
              {required.filter((k) => docsForLabel(DOC_TYPE_LABELS[k]).length > 0).length} / {required.length}
            </span>
          )}
        </div>
        <p className="mb-4 text-sm text-ink-soft">
          Solo los documentos exigidos a esta persona según su rol y el sector del sujeto obligado.
        </p>

        {esSecundario ? (
          <div className="rounded-2xl border border-dashed border-line-strong bg-canvas px-5 py-8 text-center">
            <ShieldCheck className="mx-auto h-8 w-8 text-ink-faint" strokeWidth={1.5} />
            <p className="mt-2 font-serif text-lg text-ink">No requiere documentación individual</p>
            <p className="mx-auto mt-1 max-w-sm text-sm text-ink-soft">
              La Fundación es patrimonial: a los beneficiarios secundarios no se les exige documentación.
            </p>
          </div>
        ) : docsLoading ? (
          <div className="grid gap-2.5">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-16 w-full rounded-2xl" />
            ))}
          </div>
        ) : (
          <div className="grid gap-2.5">
            {required.map((key) => {
              const label = DOC_TYPE_LABELS[key];
              const found = docsForLabel(label);
              const cumplido = found.length > 0;
              return (
                <div
                  key={key}
                  data-testid="doc-slot"
                  className="flex flex-wrap items-center gap-3.5 rounded-2xl border border-line bg-canvas px-4 py-3.5"
                >
                  <span
                    className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ${
                      cumplido ? 'bg-primary/10 text-primary-dark' : 'bg-panel text-ink-faint'
                    }`}
                  >
                    {cumplido ? (
                      <Check className="h-5 w-5" strokeWidth={2.5} />
                    ) : (
                      <FileText className="h-5 w-5" strokeWidth={1.75} />
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-ink">{label}</div>
                    <div className="truncate font-mono text-[10.5px] text-ink-faint">
                      {cumplido ? found.map((d) => d.name).join(' · ') : 'Pendiente de cargar'}
                    </div>
                  </div>
                  <div className="flex w-full items-center justify-between gap-3.5 sm:ml-auto sm:w-auto">
                    <Pill tone={cumplido ? 'green' : 'amber'} className="shrink-0">
                      {cumplido ? 'Cumplido' : 'Faltante'}
                    </Pill>
                    <Button
                      variant="outline"
                      className="shrink-0"
                      disabled={uploadMutation.isPending}
                      onClick={() => onPickFile(key)}
                    >
                      <Upload className="h-4 w-4" strokeWidth={2} /> {cumplido ? 'Reemplazar' : 'Subir'}
                    </Button>
                  </div>
                </div>
              );
            })}

            {extras.length > 0 && (
              <div className="mt-4">
                <h3 className="mb-2 flex items-center gap-1.5 text-sm font-bold text-ink">
                  <ShieldAlert className="h-4 w-4 text-tone-amber" strokeWidth={2} /> Otros documentos de la persona
                </h3>
                <div className="grid gap-2.5">
                  {extras.map((d) => (
                    <div
                      key={d.id}
                      className="flex flex-wrap items-center gap-3.5 rounded-2xl border border-line bg-canvas px-4 py-3"
                    >
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-panel text-ink-soft">
                        <FileText className="h-4 w-4" strokeWidth={1.75} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-bold text-ink">{d.name}</div>
                        <div className="truncate font-mono text-[10.5px] text-ink-faint">{d.category}</div>
                      </div>
                      <Pill tone="slate" className="shrink-0">
                        {d.estado}
                      </Pill>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      <input
        ref={fileRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => onFileChosen(e.target.files?.[0] ?? undefined)}
      />
    </>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-faint">{label}</dt>
      <dd className="mt-0.5 text-sm text-ink">{value}</dd>
    </div>
  );
}
